// Data Consistency Service - Central source of truth for enterprise data
import { format, parse, isValid } from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';

// ===========================
// ENTERPRISE DATA CONSTANTS
// ===========================

export const ENTERPRISE_CONSTANTS = {
  // Company Information
  COMPANY_NAME: 'Fresenius Medical Care',
  COMPANY_BRAND: 'Clouds on Mars',
  COMPANY_INDUSTRY: 'Healthcare',
  COMPANY_TYPE: 'Global Healthcare Enterprise',
  
  // Employee Counts - Single source of truth
  TOTAL_EMPLOYEES: 132000,
  ACTIVE_EMPLOYEES: 128500,
  CONTRACTORS: 3500,
  OPEN_POSITIONS: 1847,
  
  // Employee Distribution
  REGIONS: {
    'North America': 45000,
    'Europe': 38000,
    'Asia-Pacific': 28000,
    'Latin America': 12000,
    'Middle East & Africa': 9000
  },
  
  // Department Distribution (should sum to TOTAL_EMPLOYEES)
  DEPARTMENTS: {
    'Clinical Operations': 42000,
    'Medical Devices': 18000,
    'Research & Development': 12000,
    'Sales & Marketing': 15000,
    'Manufacturing': 20000,
    'IT & Technology': 8000,
    'Finance & Accounting': 5000,
    'Human Resources': 3000,
    'Legal & Compliance': 2000,
    'Other': 7000
  },
  
  // Performance Metrics
  METRICS: {
    averageEngagement: 72,
    averagePerformance: 3.8,
    attritionRate: 12.5,
    diversityScore: 68,
    timeToFill: 42,
    offerAcceptanceRate: 89,
    internalMobilityRate: 18
  },
  
  // Financial
  REVENUE_BILLIONS: 20.5,
  CURRENCY: 'EUR',
  FISCAL_YEAR_START: 'January',
  
  // Dates
  DATA_LAST_UPDATED: new Date('2024-12-15T00:00:00Z'),
  CURRENT_FISCAL_YEAR: 2024,
  REPORTING_PERIOD: 'Q4 2024'
} as const;

// ===========================
// DATE FORMATTING STANDARDS
// ===========================

export class DateConsistencyService {
  private static DEFAULT_TIMEZONE = 'Europe/Berlin'; // Fresenius HQ timezone
  
  // Standard date formats used across the application
  static FORMATS = {
    DISPLAY_DATE: 'MMM dd, yyyy',
    DISPLAY_DATETIME: 'MMM dd, yyyy HH:mm',
    ISO_DATE: 'yyyy-MM-dd',
    ISO_DATETIME: "yyyy-MM-dd'T'HH:mm:ss'Z'",
    SHORT_DATE: 'MM/dd/yyyy',
    LONG_DATE: 'MMMM dd, yyyy',
    TIME_ONLY: 'HH:mm',
    MONTH_YEAR: 'MMM yyyy',
    YEAR_ONLY: 'yyyy'
  };
  
  // Convert any date to standard display format
  static formatDate(date: Date | string | null, formatType: keyof typeof DateConsistencyService.FORMATS = 'DISPLAY_DATE'): string {
    if (!date) return 'N/A';
    
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (!isValid(dateObj)) return 'Invalid Date';
      
      return format(dateObj, DateConsistencyService.FORMATS[formatType]);
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  }
  
  // Convert to user's timezone
  static toUserTimezone(date: Date | string, userTimezone?: string): Date {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const timezone = userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return utcToZonedTime(dateObj, timezone);
  }
  
  // Convert from user's timezone to UTC
  static toUTC(date: Date | string, userTimezone?: string): Date {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const timezone = userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zonedTimeToUtc(dateObj, timezone);
  }
  
  // Get relative time (e.g., "2 hours ago", "3 days ago")
  static getRelativeTime(date: Date | string): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSecs < 60) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return DateConsistencyService.formatDate(dateObj);
  }
}

// ===========================
// NUMBER FORMATTING STANDARDS
// ===========================

export class NumberConsistencyService {
  // Format large numbers consistently
  static formatCount(value: number, decimals: number = 0): string {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(decimals)}K`;
    }
    return value.toLocaleString();
  }
  
  // Format percentages
  static formatPercentage(value: number, decimals: number = 1): string {
    return `${value.toFixed(decimals)}%`;
  }
  
  // Format currency
  static formatCurrency(value: number, currency: string = ENTERPRISE_CONSTANTS.CURRENCY): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }
  
  // Format with consistent decimal places
  static formatDecimal(value: number, decimals: number = 2): string {
    return value.toFixed(decimals);
  }
}

// ===========================
// DATA VALIDATION
// ===========================

export class DataValidationService {
  // Validate employee counts across components
  static validateEmployeeCount(count: number): boolean {
    return count >= 0 && count <= ENTERPRISE_CONSTANTS.TOTAL_EMPLOYEES;
  }
  
  // Validate department totals
  static validateDepartmentTotals(): boolean {
    const total = Object.values(ENTERPRISE_CONSTANTS.DEPARTMENTS).reduce((sum, count) => sum + count, 0);
    return total === ENTERPRISE_CONSTANTS.TOTAL_EMPLOYEES;
  }
  
  // Validate percentage values
  static validatePercentage(value: number): boolean {
    return value >= 0 && value <= 100;
  }
  
  // Validate date ranges
  static validateDateRange(startDate: Date, endDate: Date): boolean {
    return startDate <= endDate;
  }
  
  // Sanitize and validate email
  static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

// ===========================
// MOCK DATA CONSISTENCY
// ===========================

export class MockDataConsistencyService {
  private static seed = 12345; // Fixed seed for consistent random data
  
  // Seeded random number generator for consistent mock data
  private static seededRandom(): number {
    const x = Math.sin(this.seed++) * 10000;
    return x - Math.floor(x);
  }
  
  // Get consistent employee subset
  static getConsistentEmployeeSubset(totalRequested: number, offset: number = 0): number {
    // Always return consistent counts based on the request
    const maxAvailable = Math.min(totalRequested, ENTERPRISE_CONSTANTS.TOTAL_EMPLOYEES - offset);
    return Math.max(0, maxAvailable);
  }
  
  // Generate consistent metrics
  static getConsistentMetrics() {
    return {
      ...ENTERPRISE_CONSTANTS.METRICS,
      lastUpdated: DateConsistencyService.formatDate(ENTERPRISE_CONSTANTS.DATA_LAST_UPDATED),
      reportingPeriod: ENTERPRISE_CONSTANTS.REPORTING_PERIOD
    };
  }
  
  // Get consistent department distribution
  static getDepartmentDistribution() {
    const total = ENTERPRISE_CONSTANTS.TOTAL_EMPLOYEES;
    const distribution = Object.entries(ENTERPRISE_CONSTANTS.DEPARTMENTS).map(([dept, count]) => ({
      department: dept,
      count: count,
      percentage: (count / total) * 100
    }));
    
    return distribution;
  }
  
  // Get consistent regional distribution
  static getRegionalDistribution() {
    const total = ENTERPRISE_CONSTANTS.TOTAL_EMPLOYEES;
    const distribution = Object.entries(ENTERPRISE_CONSTANTS.REGIONS).map(([region, count]) => ({
      region: region,
      count: count,
      percentage: (count / total) * 100
    }));
    
    return distribution;
  }
}

// ===========================
// CACHE MANAGEMENT
// ===========================

export class CacheConsistencyService {
  private static cache = new Map<string, { data: any; timestamp: number }>();
  private static CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  
  // Set cache with TTL
  static set(key: string, data: any, ttl?: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now() + (ttl || this.CACHE_TTL)
    });
  }
  
  // Get from cache if valid
  static get(key: string): any | null {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    if (Date.now() > cached.timestamp) {
      this.cache.delete(key);
      return null;
    }
    
    return cached.data;
  }
  
  // Clear specific cache or all
  static clear(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }
  
  // Check if cache is valid
  static isValid(key: string): boolean {
    const cached = this.cache.get(key);
    return cached ? Date.now() <= cached.timestamp : false;
  }
}

// ===========================
// MAIN SERVICE EXPORT
// ===========================

class DataConsistencyService {
  // Get consistent employee count
  static getTotalEmployees(): number {
    return ENTERPRISE_CONSTANTS.TOTAL_EMPLOYEES;
  }
  
  // Get active employees
  static getActiveEmployees(): number {
    return ENTERPRISE_CONSTANTS.ACTIVE_EMPLOYEES;
  }
  
  // Format any date consistently
  static formatDate(date: Date | string | null, formatType?: keyof typeof DateConsistencyService.FORMATS): string {
    return DateConsistencyService.formatDate(date, formatType);
  }
  
  // Format numbers consistently
  static formatNumber(value: number, type: 'count' | 'percentage' | 'currency' | 'decimal' = 'count', options?: any): string {
    switch (type) {
      case 'percentage':
        return NumberConsistencyService.formatPercentage(value, options?.decimals);
      case 'currency':
        return NumberConsistencyService.formatCurrency(value, options?.currency);
      case 'decimal':
        return NumberConsistencyService.formatDecimal(value, options?.decimals);
      default:
        return NumberConsistencyService.formatCount(value, options?.decimals);
    }
  }
  
  // Get consistent metrics
  static getMetrics() {
    return MockDataConsistencyService.getConsistentMetrics();
  }
  
  // Validate data
  static validate(type: 'employeeCount' | 'percentage' | 'email' | 'dateRange', ...args: any[]): boolean {
    switch (type) {
      case 'employeeCount':
        return DataValidationService.validateEmployeeCount(args[0]);
      case 'percentage':
        return DataValidationService.validatePercentage(args[0]);
      case 'email':
        return DataValidationService.validateEmail(args[0]);
      case 'dateRange':
        return DataValidationService.validateDateRange(args[0], args[1]);
      default:
        return false;
    }
  }
  
  // Cache operations
  static cache = {
    set: (key: string, data: any, ttl?: number) => CacheConsistencyService.set(key, data, ttl),
    get: (key: string) => CacheConsistencyService.get(key),
    clear: (key?: string) => CacheConsistencyService.clear(key),
    isValid: (key: string) => CacheConsistencyService.isValid(key)
  };
  
  // Get all constants for reference
  static getConstants() {
    return ENTERPRISE_CONSTANTS;
  }
}

export default DataConsistencyService;